import React, { useCallback, useEffect, useRef, useState } from 'react';
import { checkUserExists, getAuthenticatedUserNode } from '@/utils/gun-utils';
import {
  getLegacyAgent,
  getLegacySignedMessage,
} from '@/utils/identity-utils/key-derivation-utils';
import { IdentityInstance } from '@/utils/identity-utils/identity-contract-module';
import { getLocalPortalKeys } from '@/utils/portal-utils';
import { IPortal } from '@/types';
import { ModalContent } from './modal-content';
import { useAccountContext } from '../providers/account-provider';
import { KeyStoreInstance } from '@/utils/identity-utils/key-store';
import { Hex } from 'viem';
import { captureException } from '@sentry/nextjs';
import { LoadingSpinner } from './loading-spinner';
import { Button, LucideIcon } from '@fileverse/ui';
import { DynamicModal } from '@fileverse/ui';
import { useLogout } from '@/hooks/useLogout';

const getLegacyUserNodeSafely = async (signedMessage: Hex) => {
  try {
    const { node: userNode } =
      (await getAuthenticatedUserNode(signedMessage, true)) || {};
    return { node: userNode };
  } catch (error) {
    captureException(error);
    console.error(error);
  }
};

export const IdentitySetupModal = () => {
  const [modalStates, setModalStates] = useState({
    isInitialModalLoading: false,
    isMigrationModalOpen: false,
    isGunYesPortalKeysNull: false,
  });
  const [legacyPortalKeys, setLegacyPortalKeys] = useState<IPortal | null>(
    null
  );
  const { identityAlreadyExists, onCompleteSetup, identityInitialized } =
    useAccountContext();
  const isRunningRef = useRef(false);
  const [flow, setFlow] = useState<'legacy' | 'new' | null>(null);
  const { onLogout } = useLogout();
  const startLegacyModalInit = useCallback(async () => {
    KeyStoreInstance.isLegacyUser = true;
    const walletClient = IdentityInstance.getWalletClient();
    const signedMessage = await getLegacySignedMessage(walletClient);

    const { node: userNode } =
      (await getLegacyUserNodeSafely(signedMessage)) || {};

    KeyStoreInstance.gunUserNode = userNode;

    const legacySmartAccountClient = await getLegacyAgent(walletClient);

    const portalKeys = await getLocalPortalKeys(
      legacySmartAccountClient.account.address,
      userNode
    );

    if (portalKeys && !identityInitialized) {
      setLegacyPortalKeys(portalKeys);
      setModalStates({
        ...modalStates,
        isMigrationModalOpen: true,
        isGunYesPortalKeysNull: false,
      });
      setFlow('legacy');
    } else {
      setModalStates({
        isMigrationModalOpen: false,
        isGunYesPortalKeysNull: true,
        isInitialModalLoading: false,
      });
    }
  }, []);

  const intializeModal = useCallback(async () => {
    setModalStates((prev) => ({
      ...prev,
      isInitialModalLoading: true,
    }));

    if (isRunningRef.current) return;
    isRunningRef.current = true;

    const walletClient = IdentityInstance.getWalletClient();
    const walletAddress = walletClient.account.address;
    const isLegacyUser = await checkUserExists(walletAddress);
    if (isLegacyUser) {
      await startLegacyModalInit();
    } else {
      setFlow('new');
    }
    setModalStates((prev) => ({
      ...prev,
      isInitialModalLoading: false,
    }));
  }, [startLegacyModalInit]);

  const handleFlowChange = useCallback((flow: 'legacy' | 'new' | null) => {
    setFlow(flow);
    setModalStates((prev) => ({
      ...prev,
      isGunYesPortalKeysNull: false,
    }));
  }, []);

  useEffect(() => {
    intializeModal();
  }, [intializeModal]);

  if (modalStates.isInitialModalLoading) {
    return <LoadingSpinner />;
  }

  if (modalStates.isGunYesPortalKeysNull) {
    return (
      <DynamicModal
        open={modalStates.isGunYesPortalKeysNull}
        onOpenChange={(open) => {
          setModalStates({
            ...modalStates,
            isGunYesPortalKeysNull: open,
          });
        }}
        className="md:!max-w-[23rem] !pb-0 !rounded-lg"
        contentClassName="!py-6"
        content={
          <div className="flex flex-col gap-4 justify-center items-center px-4 text-center">
            <div className="flex items-center justify-center px-[9px] py-[5px] rounded color-bg-secondary">
              <span className="text-heading-lg">👩‍🏭</span>
            </div>
            <h3 className="text-heading-sm">
              Please log in from your original device
            </h3>
            <p className="text-body-sm color-text-secondary">
              Your dDocs App is getting a super upgrade in performance and
              reliability. Learn more{' '}
              <a
                href="/infra-upgrade"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#5C0AFF]"
              >
                {' '}
                here!
              </a>{' '}
            </p>
            <div className="flex flex-col justify-center items-center gap-3 w-full p-3 rounded-lg color-bg-secondary color-text-default text-body-sm">
              <p className="text-heading-xsm">Recommended</p>
              <p className="flex flex-col gap-2">
                {
                  'Please log in from the original device you used to create this dDocs account. Ty :)'
                }
                <Button
                  className="w-full gap-2"
                  onClick={() => {
                    onLogout();
                    window.location.reload();
                  }}
                >
                  <LucideIcon name="LogOut" size="md" />
                  Log out
                </Button>
              </p>
            </div>
            <div className="flex flex-col justify-center items-center gap-3 w-full p-3 rounded-lg bg-[#FFF1F2] color-text-default text-body-sm">
              <p className="text-heading-xsm">Not recommended</p>
              <p className="flex flex-col gap-2">
                {
                  'The account upgrade may fail if you’re not using your original device, which could result in losing your documents.'
                }
                <Button
                  className="w-full gap- bg-transparent"
                  variant="secondary"
                  onClick={() => handleFlowChange('legacy')}
                >
                  I understand and I wish to proceed
                </Button>
              </p>
            </div>
          </div>
        }
      />
    );
  }

  return (
    <div className="fixed bottom-8 right-4">
      <ModalContent
        flow={flow}
        modalStates={modalStates}
        setModalStates={setModalStates}
        portalKeys={legacyPortalKeys}
        identityAlreadyExists={identityAlreadyExists}
        onCompleteSetup={onCompleteSetup}
      />
    </div>
  );
};
